import { ReactNode, ReactElement, Children } from "react";
import { svgPathBbox } from "svg-path-bbox";

interface PathProps { d: string }
interface CircleProps { cx: number; cy: number; r: number }

export const collectShapes = (nodes: ReactNode) => {
  const paths: ReactElement<PathProps>[] = [];
  const circles: ReactElement<CircleProps>[] = [];

  Children.forEach(nodes, node => {
    if (!node || typeof node !== "object") return;

    const el = node as ReactElement<unknown>;
    const props = el.props as Record<string, unknown>;

    if (el.type === "path" && typeof props.d === "string") {
      paths.push(el as ReactElement<PathProps>);
    } else if (
      el.type === "circle" &&
      typeof props.cx === "number" &&
      typeof props.cy === "number" &&
      typeof props.r === "number"
    ) {
      circles.push(el as ReactElement<CircleProps>);
    } else if (props?.children) {
      const inner = collectShapes(props.children as ReactNode); // ← cast here
      paths.push(...inner.paths);
      circles.push(...inner.circles);
    }
  });

  return { paths, circles };
};

export const computePathBounds = (paths: ReactElement<PathProps>[]) => {
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;

  paths.forEach(p => {
    const [x1, y1, x2, y2] = svgPathBbox(p.props.d);
    minX = Math.min(minX, x1);
    minY = Math.min(minY, y1);
    maxX = Math.max(maxX, x2);
    maxY = Math.max(maxY, y2);
  });

  return { minX, minY, maxX, maxY };
};

export const computeCircleBounds = (circles: ReactElement<CircleProps>[]) => {
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;

  circles.forEach(c => {
    const { cx, cy, r } = c.props;
    minX = Math.min(minX, cx - r);
    minY = Math.min(minY, cy - r);
    maxX = Math.max(maxX, cx + r);
    maxY = Math.max(maxY, cy + r);
  });

  return { minX, minY, maxX, maxY };
};
